/* Brand theme injection — turns brand.config.js `theme` into CSS-variable
   overrides for every surface (form, console, broker portal, link pages).

   The stylesheets keep their shipped values as :root defaults; this module
   only injects an override <style> for the tokens whose configured value
   differs from the shipped default, so an untouched brand.config.js renders
   pixel-identical to the shipped design. Ramp shades the config doesn't name
   (the console's pink-200…400 / 800…900) are derived with color-mix(). */
import BRAND from "../brand.config.js";
import { RUNTIME_BRAND } from "./runtimeBrand.js";

export const THEME_DEFAULTS = {
  accent: "#2864F0",
  accentDark: "#1F54D6",
  accentDarker: "#1946B8",
  accentTint: "#DCE8FF",
  accentTint2: "#EFF4FF",
  ink: "#162033",          // dark brand surface (logo tile, tooltips, on-brand blocks)
  surface: "#F4F7FB",      // page ground behind the cards
  fontDisplay: "Inter",
  fontBody: "Inter",
  googleFonts: [
    "Inter:wght@400;500;600;700;800",
  ],
};

/* The server injects the tenant's brand before any module runs, so a
   multi-brand deployment themes on the FIRST paint rather than correcting
   itself after a fetch. */
const RUNTIME_THEME = RUNTIME_BRAND?.theme || null;
export const THEME = { ...THEME_DEFAULTS, ...(BRAND.theme || {}), ...(RUNTIME_THEME || {}) };

const same = (k) => String(THEME[k]).toLowerCase() === String(THEME_DEFAULTS[k]).toLowerCase();

/* The :root override block, or "" when the config matches the shipped theme.
   Takes the theme as an argument so the shipped-defaults case (which must emit
   nothing at all) is testable without reaching for a different brand.config. */
export function brandThemeCss(theme = THEME) {
  const lines = [];
  const same = (k) => String(theme[k]).toLowerCase() === String(THEME_DEFAULTS[k]).toLowerCase();
  const accentChanged = !(same("accent") && same("accentDark") && same("accentDarker") && same("accentTint") && same("accentTint2"));
  if (accentChanged) {
    // Shared names (form widget, broker portal, upload/consent pages)
    lines.push(
      `--coral:${theme.accent};`,
      `--coral-600:${theme.accentDark};`,
      `--coral-700:${theme.accentDarker};`,
      `--coral-tint:${theme.accentTint};`,
      `--coral-tint-2:${theme.accentTint2};`,
      // Console ramp (admin/styles/tokens.css)
      `--pink-500:${theme.accent};`,
      `--pink-600:${theme.accentDark};`,
      `--pink-700:${theme.accentDarker};`,
      `--pink-100:${theme.accentTint};`,
      `--pink-50:${theme.accentTint2};`,
      `--pink-200:color-mix(in srgb, ${theme.accent} 25%, white);`,
      `--pink-300:color-mix(in srgb, ${theme.accent} 45%, white);`,
      `--pink-400:color-mix(in srgb, ${theme.accent} 70%, white);`,
      `--pink-800:color-mix(in srgb, ${theme.accent} 75%, black);`,
      `--pink-900:color-mix(in srgb, ${theme.accent} 55%, black);`,
      // The form's accent glow is a baked rgba in brand.css — re-derive it so a
      // re-branded accent never leaves the old hue glowing under the buttons.
      `--sh-coral:0 6px 16px -4px color-mix(in srgb, ${theme.accent} 45%, transparent);`,
    );
  }
  if (!same("ink")) {
    // Dark brand surface + the ramp the console derives from it.
    lines.push(
      `--espresso-900:${theme.ink};`,
      `--espresso-950:color-mix(in srgb, ${theme.ink} 80%, black);`,
      `--espresso-800:color-mix(in srgb, ${theme.ink} 88%, white);`,
      `--espresso-700:color-mix(in srgb, ${theme.ink} 76%, white);`,
    );
  }
  if (!same("surface")) {
    // Page ground: --paper is the console's name for it, --bg the one the form,
    // broker portal and applicant link pages use.
    lines.push(`--paper:${theme.surface};`, `--bg:${theme.surface};`);
  }
  if (!same("fontDisplay") || !same("fontBody")) {
    lines.push(
      `--font-display:'${theme.fontDisplay}', -apple-system, system-ui, sans-serif;`,
      `--font-body:'${theme.fontBody}', -apple-system, system-ui, sans-serif;`,
      // Console family token
      `--font-sans:'${theme.fontDisplay}', ui-sans-serif, system-ui, -apple-system, sans-serif;`,
    );
  }
  return lines.length ? `:root{${lines.join("")}}` : "";
}

/* Idempotent: inject the override style (and the Google Fonts link when the
   configured faces differ from the shipped ones). Call once per entry. */
export function injectBrandTheme() {
  if (typeof document === "undefined") return;
  // The server already themed this document for its tenant; re-injecting would
  // just append a second, later-winning block saying the same thing.
  if (document.getElementById("pf-tenant-theme")) return;
  const css = brandThemeCss();
  if (css && !document.getElementById("pf-brand-theme")) {
    const style = document.createElement("style");
    style.id = "pf-brand-theme";
    style.textContent = css;
    document.head.appendChild(style);
  }
  if ((!same("fontDisplay") || !same("fontBody")) && THEME.googleFonts?.length
      && !document.getElementById("pf-brand-fonts")) {
    const link = document.createElement("link");
    link.id = "pf-brand-fonts";
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?" + THEME.googleFonts.map((f) => "family=" + f).join("&") + "&display=swap";
    document.head.appendChild(link);
  }
}
